import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    ButtonBase,
    Button,
    Collapse,
    CssBaseline,
    Snackbar,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AtSign,
    BadgeCheck,
    Bike,
    Building2,
    Calendar,
    Car,
    Check,
    CheckCircle2,
    ChevronDown,
    ClipboardCheck,
    Clock,
    FileText,
    Globe,
    MapPin,
    MessageCircle,
    Package,
    Phone,
    RefreshCw,
    SearchX,
    ShoppingBag,
    Unlink,
    User,
    WifiOff,
    Wrench,
    XCircle,
} from 'lucide-react';
import { httpClient } from './common/httpclient';
import { formatPhone } from './common/formatters';
import { buildAppTheme } from './theme/theme';
import ToastAlert from './component/alert/ToastAlert';

/**
 * Tela pública do cliente (`/ordem/:numero_ordem?t=<token>`) — REDESIGN,
 * `prototipo-ordem-publica-cliente.html` (FRONTEND-TASKS.md item 1,
 * "Autenticação & telas públicas").
 *
 * Superfície de design própria: sem sidebar/topbar do ERP, mobile-first
 * (cliente abre pelo WhatsApp/SMS). Segue `prefers-color-scheme` (sem toggle
 * para o cliente final, como pede o protótipo) — por isso embrulha o conteúdo
 * num `ThemeProvider` próprio em vez de usar o modo salvo em
 * `localStorage.theme` do ERP.
 *
 * Dados: somente campos reais de `GET /ordem/{id}?t=`
 * (`fv-api/serviceorder/customer_order_viewer.go`) — `order_details`,
 * `products[]`, `services[]`, `values`.
 *
 * Lacunas conhecidas (dependem de backend, NÃO inventadas aqui):
 *  - Tarefas/checklist por serviço: a API pública não retorna. O accordion
 *    "Andamento do serviço" só aparece se um item de `services[]` trouxer
 *    `tasks: [{ name, done }]` (forma proposta no protótipo); sem esse dado o
 *    serviço é uma linha simples, nunca um accordion vazio.
 *  - Histórico de status com data/hora: só existe o status atual; o stepper
 *    de 4 etapas é derivado dele (sem timestamps).
 *  - Ordem inexistente hoje tende a virar 500 com texto de erro do banco (o
 *    ideal seria 404 JSON); o 500 cai em "Não foi possível carregar".
 *  - O payload público ainda inclui `customer_document`,
 *    `external_unit_price`/`external_amount_total` e `signature_image`; a tela
 *    não exibe nada disso (recomenda-se removê-los da API).
 */

// Breakpoints do protótipo (container queries → media queries de viewport).
const MQ_520 = '@media (min-width:520px)';
const MQ_860 = '@media (min-width:860px)';

const FOOT_TEXT = 'Acompanhamento de ordem de serviço · FlowVendas';

// ---------------------------------------------------------------------------
// Helpers de dados
// ---------------------------------------------------------------------------

function normalizeStatus(status) {
    return String(status ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();
}

// Status da ordem (§4). "Paga" (valor da API) -> "Faturada" (rótulo da UI).
// `step` alimenta o stepper de 4 etapas; -1 = terminal cancelado (sem stepper).
const STATUS_META = {
    'nao iniciada': {
        key: 'nao_iniciada', label: 'Não Iniciada', step: 0, Icon: Clock,
        title: 'Recebemos a sua ordem',
        msg: 'Ela está na fila e será iniciada em breve. Volte a este link a qualquer momento para acompanhar o andamento.',
    },
    'em execucao': {
        key: 'em_execucao', label: 'Em Execução', step: 1, Icon: Wrench,
        title: 'Serviço em andamento',
        msg: 'Nossa equipe já está trabalhando no seu serviço. Volte a este link a qualquer momento para acompanhar.',
    },
    finalizada: {
        key: 'finalizada', label: 'Finalizada', step: 2, Icon: CheckCircle2,
        title: 'Serviço concluído',
        msg: 'Seu serviço foi finalizado. Entre em contato com a empresa para combinar a retirada.',
    },
    paga: {
        key: 'paga', label: 'Faturada', step: 3, Icon: BadgeCheck,
        title: 'Ordem faturada',
        msg: 'Esta ordem foi encerrada e o pagamento foi registrado. Obrigado pela preferência!',
    },
    faturada: {
        key: 'paga', label: 'Faturada', step: 3, Icon: BadgeCheck,
        title: 'Ordem faturada',
        msg: 'Esta ordem foi encerrada e o pagamento foi registrado. Obrigado pela preferência!',
    },
    cancelada: {
        key: 'cancelada', label: 'Cancelada', step: -1, Icon: XCircle,
        title: 'Ordem cancelada',
        msg: 'Esta ordem foi cancelada. Se tiver qualquer dúvida, fale com a empresa pelos contatos abaixo.',
    },
};

function getStatusMeta(status) {
    const known = STATUS_META[normalizeStatus(status)];
    if (known) return known;
    // Status que o front não conhece: mostra o texto cru da API, sem stepper.
    return {
        key: 'desconhecido', label: status || '—', step: -2, Icon: Clock,
        title: 'Acompanhe a sua ordem',
        msg: 'Volte a este link a qualquer momento para acompanhar o andamento do seu serviço.',
    };
}

const STEP_LABELS = ['Ordem aberta', 'Em execução', 'Concluída', 'Faturada'];

const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * A API devolve valores já formatados pt-BR e sem "R$" (ex. "1.086,00").
 * O `replace(',', '.')` antigo quebrava de R$ 1.000 para cima (virava
 * "1.086.00" -> 1.086). Aqui: com vírgula = pt-BR (tira os pontos de milhar,
 * vírgula vira ponto); sem vírgula = número puro.
 */
function parseMoney(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (value === undefined || value === null || value === '') return 0;
    const str = String(value).trim();
    const normalized = str.includes(',') ? str.replace(/\./g, '').replace(',', '.') : str;
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
}

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatCurrency = (value) => brlFormatter.format(parseMoney(value));

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** "18/09/2026 09:12:40" -> { date, dow, time } | null (sem data / "01/01/0001"). */
function parseDateTime(value) {
    if (!value || /\/0001/.test(value)) return null;
    const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
    if (!match) return null;
    const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    return {
        date: `${match[1]}/${match[2]}/${match[3]}`,
        dow: WEEKDAYS[date.getDay()],
        time: match[4] ? `${match[4]}:${match[5]}` : '',
    };
}

/** Telefone BR -> dígitos com DDI 55 (sem duplicar se já vier com 55). */
function toInternationalPhone(value) {
    const d = digitsOnly(value);
    if (!d) return '';
    return d.length >= 12 && d.startsWith('55') ? d : `55${d}`;
}

const isImageSource = (value) => /^(https?:\/\/|data:image\/)/i.test(String(value || '').trim());

// ---------------------------------------------------------------------------
// Tela (raiz): tema próprio do cliente final
// ---------------------------------------------------------------------------

export default function CustomerOrderDisplayScreen() {
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
    const publicTheme = useMemo(() => buildAppTheme(prefersDark ? 'dark' : 'light'), [prefersDark]);

    return (
        <ThemeProvider theme={publicTheme}>
            <CssBaseline />
            <OrderScreen />
        </ThemeProvider>
    );
}

function OrderScreen() {
    const theme = useTheme();
    const { numero_ordem } = useParams();
    const [queryParams] = useSearchParams();
    const token = queryParams.get('t');

    // loading | ok | invalid | notfound | error
    const [view, setView] = useState('loading');
    const [orderData, setOrderData] = useState(null);
    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [orderValues, setOrderValues] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

    const fetchOrderData = useCallback(async ({ silent = false } = {}) => {
        try {
            if (silent) setRefreshing(true);
            else setView('loading');

            const orderResponse = await httpClient.get(`/ordem/${numero_ordem}?t=${encodeURIComponent(token || '')}`);
            const data = orderResponse.data;

            if (orderResponse.status === 200 && data?.order_details) {
                setOrderData(data.order_details);
                setProducts(data.products || []);
                setServices(data.services || []);
                setOrderValues(data.values);
                setUpdatedAt(new Date());
                setView('ok');
                if (silent) showToast('Ordem atualizada');
                return;
            }

            // Refresh manual que falhou: mantém o que já está na tela.
            if (silent) {
                showToast('Não foi possível atualizar agora. Tente novamente.', 'error');
                return;
            }

            if (orderResponse.status === 400) setView('invalid');
            else if (orderResponse.status === 404 || orderResponse.status === 200) setView('notfound');
            else setView('error');
        } catch (err) {
            console.error('Erro ao carregar ordem:', err);
            if (silent) showToast('Não foi possível atualizar agora. Tente novamente.', 'error');
            else setView('error');
        } finally {
            if (silent) setRefreshing(false);
        }
    }, [numero_ordem, token]);

    useEffect(() => {
        if (numero_ordem) {
            fetchOrderData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numero_ordem]);

    // O `index.css` do ERP fixa um fundo próprio no <body>; alinha com o tema
    // público (aparece no overscroll/bounce do celular) e restaura ao sair.
    useEffect(() => {
        const previous = document.body.style.backgroundColor;
        document.body.style.backgroundColor = theme.palette.background.default;
        return () => { document.body.style.backgroundColor = previous; };
    }, [theme.palette.background.default]);

    let content;
    if (view === 'loading') {
        content = <LoadingView />;
    } else if (view !== 'ok' || !orderData) {
        content = <FeedbackView kind={view === 'ok' ? 'notfound' : view} onRetry={() => fetchOrderData()} />;
    } else {
        content = (
            <OrderView
                order={orderData}
                products={products}
                services={services}
                values={orderValues}
                updatedAt={updatedAt}
                refreshing={refreshing}
                onRefresh={() => fetchOrderData({ silent: true })}
            />
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', display: 'flex', flexDirection: 'column',
                '@media print': { '& .no-print': { display: 'none !important' } },
            }}
        >
            {content}
            <Snackbar
                open={toast.open}
                autoHideDuration={2800}
                onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setToast((prev) => ({ ...prev, open: false }));
                }}
                className="no-print"
                sx={{ bottom: { xs: 88, md: 24 } }}
            >
                <ToastAlert severity={toast.severity}>{toast.message}</ToastAlert>
            </Snackbar>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Estado "ok"
// ---------------------------------------------------------------------------

function OrderView({ order, products, services, values, updatedAt, refreshing, onRefresh }) {
    const meta = getStatusMeta(order.status);
    const isCancelled = meta.key === 'cancelada';
    const entrada = parseDateTime(order.order_date);
    const entrega = parseDateTime(order.order_delivery_date);

    const cityState = order.enterprise_city && order.enterprise_state
        ? `${order.enterprise_city}/${order.enterprise_state}`
        : order.enterprise_city || order.enterprise_state || '';
    const addressText = [
        order.enterprise_address,
        order.enterprise_number_address || null,
        order.enterprise_neighborhood,
        cityState,
    ].filter(Boolean).join(', ');

    const contactPhone = order.enterprise_cellphone || order.enterprise_phone_number;
    const waHref = order.enterprise_cellphone
        ? `https://wa.me/${toInternationalPhone(order.enterprise_cellphone)}?text=${encodeURIComponent(`Olá! Estou acompanhando a ordem de serviço nº ${order.number}.`)}`
        : '';
    const telHref = contactPhone ? `tel:+${toInternationalPhone(contactPhone)}` : '';

    const itemCount = services.length + products.length;
    const showProductDiscount = parseMoney(values?.total_product_item_discount) > 0;

    return (
        <>
            {order.order_banner && <BannerStrip value={order.order_banner} />}
            <BrandBar order={order} />

            <Box
                component="main"
                sx={{
                    width: '100%', maxWidth: 1040, mx: 'auto', display: 'grid', gap: '14px',
                    p: '16px 16px 24px',
                    [MQ_520]: { p: '20px 24px 28px' },
                    [MQ_860]: { gap: '18px', p: '24px 24px 32px' },
                }}
            >
                <Hero
                    order={order}
                    meta={meta}
                    entrada={entrada}
                    entrega={entrega}
                    updatedAt={updatedAt}
                    refreshing={refreshing}
                    onRefresh={onRefresh}
                />

                <Box
                    sx={{
                        display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: '14px',
                        [MQ_860]: { gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '18px', alignItems: 'start' },
                    }}
                >
                    {/* Mobile: as colunas "somem" (display: contents) e cada card se ordena por `order`. */}
                    <Box sx={{ display: 'contents', [MQ_860]: { display: 'flex', flexDirection: 'column', gap: '18px' } }}>
                        {order.vehicle_model && <VehicleCard order={order} />}
                        {order.defect_reason && (
                            <Card sx={{ order: 2 }}>
                                <CardHead icon={<ClipboardCheck size={16} />} title="Motivo/Defeito" />
                                <Typography sx={{ m: 0, fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {order.defect_reason}
                                </Typography>
                            </Card>
                        )}
                        {order.description && (
                            <Card sx={{ order: 2 }}>
                                <CardHead icon={<ClipboardCheck size={16} />} title="Serviço solicitado" />
                                <Typography sx={{ m: 0, fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {order.description}
                                </Typography>
                            </Card>
                        )}
                        <Card sx={{ order: 3 }}>
                            <CardHead
                                icon={<ShoppingBag size={16} />}
                                tone="purple"
                                title="Itens da ordem"
                                count={`${itemCount} ${itemCount === 1 ? 'item' : 'itens'}`}
                            />
                            {services.length > 0 && (
                                <>
                                    <SectionLabel first>Serviços</SectionLabel>
                                    {services.map((item) => <ItemRow key={item.id} item={item} isService />)}
                                </>
                            )}
                            {products.length > 0 && (
                                <>
                                    <SectionLabel first={services.length === 0}>Produtos e peças</SectionLabel>
                                    {products.map((item) => <ItemRow key={item.id} item={item} />)}
                                </>
                            )}
                            {itemCount === 0 && (
                                <Box sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 12.5, py: '26px', px: '10px' }}>
                                    Nenhum item encontrado nesta ordem.
                                </Box>
                            )}
                        </Card>
                        <Card sx={{ order: 5 }}>
                            <CardHead icon={<User size={16} />} title="Dados do cliente" />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                                <KeyValue label="Nome" value={order.customer_name || 'Não informado'} />
                                {order.customer_cellphone && <KeyValue label="Contato" value={formatPhone(order.customer_cellphone)} />}
                                {order.customer_address && <KeyValue label="Endereço" value={order.customer_address} full />}
                            </Box>
                        </Card>
                        {order.order_terms && <TermsCard terms={order.order_terms} />}
                    </Box>

                    <Box sx={{ display: 'contents', [MQ_860]: { display: 'flex', flexDirection: 'column', gap: '18px', position: 'sticky', top: 16 } }}>
                        {values && (
                            <SummaryCard values={values} showDiscount={showProductDiscount} muted={isCancelled} />
                        )}
                        <CompanyCard
                            order={order}
                            addressText={addressText}
                            waHref={waHref}
                            telHref={telHref}
                        />
                    </Box>
                </Box>
            </Box>

            <PoweredBy />

            {(waHref || telHref) && (
                <Box
                    className="no-print"
                    sx={(t) => ({
                        position: 'sticky', bottom: 0, zIndex: 20, mt: 'auto',
                        bgcolor: 'background.paper', borderTop: `1px solid ${t.palette.divider}`,
                        p: '10px 16px calc(10px + env(safe-area-inset-bottom))',
                        display: 'flex', gap: '10px', boxShadow: '0 -6px 20px rgba(15,23,42,.08)',
                        [MQ_860]: { display: 'none' },
                    })}
                >
                    {waHref && <ContactButton kind="whatsapp" href={waHref} large />}
                    {telHref && <ContactButton kind="phone" href={telHref} large />}
                </Box>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// Marca (banner + barra da empresa)
// ---------------------------------------------------------------------------

/**
 * `order_banner` (Configurações > "Banner de impressão") é, hoje, um TEXTO
 * livre, não uma imagem — o protótipo desenha uma faixa. Se algum dia vier
 * uma URL/data-URI de imagem, é renderizada como imagem (mesma faixa).
 */
function BannerStrip({ value }) {
    const asImage = isImageSource(value);
    return (
        <Box
            sx={(t) => ({
                bgcolor: 'info.bg', color: 'primary.main', borderBottom: `1px solid ${t.palette.divider}`,
                textAlign: 'center', overflow: 'hidden',
                ...(asImage
                    ? { height: 96, [MQ_520]: { height: 140 }, [MQ_860]: { height: 180 } }
                    : { px: 2, py: '10px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.45, whiteSpace: 'pre-wrap' }),
            })}
        >
            {asImage
                ? <Box component="img" src={value} alt="Banner da empresa" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : value}
        </Box>
    );
}

function BrandBar({ order }) {
    const [logoFailed, setLogoFailed] = useState(false);
    const initial = order.enterprise_name ? order.enterprise_name.charAt(0).toUpperCase() : 'E';
    const showLogo = !!order.order_logo && !logoFailed;

    return (
        <Box
            component="header"
            sx={(t) => ({ bgcolor: 'background.paper', borderBottom: `1px solid ${t.palette.divider}` })}
        >
            <Box
                sx={{
                    maxWidth: 1040, mx: 'auto', display: 'flex', alignItems: 'center', gap: '12px', p: '14px 16px',
                    [MQ_520]: { p: '16px 24px' },
                }}
            >
                <Box
                    sx={(t) => ({
                        width: 48, height: 48, borderRadius: '14px', flexShrink: 0, overflow: 'hidden',
                        backgroundImage: t.palette.gradient.brand, color: '#fff', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 19,
                        boxShadow: '0 4px 14px rgba(37,99,235,.28)',
                    })}
                >
                    {showLogo ? (
                        <Box
                            component="img"
                            src={order.order_logo}
                            alt={`Logo da ${order.enterprise_name || 'empresa'}`}
                            onError={() => setLogoFailed(true)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : initial}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.25 }}>
                        {order.enterprise_name || 'Empresa'}
                    </Typography>
                    {order.enterprise_document && (
                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{order.enterprise_document}</Typography>
                    )}
                </Box>
                {order.enterprise_cellphone && (
                    <Box sx={{ display: 'none', alignItems: 'center', gap: '8px', fontSize: 12.5, color: 'text.secondary', fontWeight: 600, [MQ_860]: { display: 'flex' } }}>
                        <Phone size={15} />
                        {formatPhone(order.enterprise_cellphone)}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function PoweredBy() {
    return (
        <Box
            sx={{
                maxWidth: 1040, mx: 'auto', width: '100%', p: '4px 16px 28px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: 11,
                color: 'text.disabled', fontWeight: 600,
            }}
        >
            <Box
                sx={(t) => ({
                    width: 18, height: 18, borderRadius: '6px', backgroundImage: t.palette.gradient.brand,
                    color: '#fff', fontSize: 8.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
                })}
            >
                FV
            </Box>
            {FOOT_TEXT}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Hero de status
// ---------------------------------------------------------------------------

function Hero({ order, meta, entrada, entrega, updatedAt, refreshing, onRefresh }) {
    const isCancelled = meta.key === 'cancelada';
    const StatusIcon = meta.Icon;

    // Variáveis CSS locais: o hero é sempre sobre o gradiente da marca (texto
    // branco, qualquer que seja o tema); o cancelado vira um card neutro; e o
    // print troca tudo para tinta escura (navegadores não imprimem fundo).
    const heroVars = isCancelled
        ? {
            '--h-fg': 'var(--fv-text)', '--h-fg2': 'var(--fv-text2)', '--h-tile': 'var(--fv-surface2)',
            '--h-tile-b': 'var(--fv-border)', '--h-ink': '#fff',
        }
        : {
            '--h-fg': '#fff', '--h-fg2': 'rgba(255,255,255,.82)', '--h-tile': 'rgba(255,255,255,.14)',
            '--h-tile-b': 'rgba(255,255,255,.24)', '--h-ink': 'var(--fv-brand)',
        };

    return (
        <Box
            component="section"
            sx={(t) => ({
                '--fv-text': t.palette.text.primary,
                '--fv-text2': t.palette.text.secondary,
                '--fv-surface2': t.palette.background.subtle,
                '--fv-border': t.palette.divider,
                '--fv-brand': t.palette.primary.main,
                ...heroVars,
                position: 'relative', overflow: 'hidden', color: 'var(--h-fg)',
                borderRadius: '20px', p: '18px 18px 16px', boxShadow: t.customShadows.md,
                ...(isCancelled
                    ? { bgcolor: 'background.paper', border: `1px solid ${t.palette.divider}` }
                    : {
                        backgroundImage: t.palette.gradient.brand,
                        '&::after': {
                            content: '""', position: 'absolute', right: -60, top: -60, width: 200, height: 200,
                            borderRadius: '50%', bgcolor: 'rgba(255,255,255,.08)', pointerEvents: 'none',
                        },
                    }),
                [MQ_520]: { p: '22px 24px 18px' },
                '@media print': {
                    '--h-fg': '#000', '--h-fg2': '#333', '--h-tile': '#f3f4f6', '--h-tile-b': '#ccc', '--h-ink': '#fff',
                    backgroundImage: 'none', backgroundColor: '#fff', border: '1px solid #ccc', boxShadow: 'none',
                    '&::after': { display: 'none' },
                },
            })}
        >
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--h-fg2)' }}>
                    Ordem de serviço{' '}
                    <Box component="b" sx={{ color: 'var(--h-fg)', fontSize: 12.5, letterSpacing: '.02em' }}>Nº {order.number}</Box>
                </Typography>
                <Box
                    sx={(t) => ({
                        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 11.5, fontWeight: 700,
                        px: '11px', py: '5px', borderRadius: '20px', whiteSpace: 'nowrap',
                        bgcolor: isCancelled ? 'error.bg' : 'rgba(255,255,255,.22)',
                        color: isCancelled ? 'error.main' : '#fff',
                        '@media print': { bgcolor: t.palette.grey[200], color: '#000' },
                    })}
                >
                    <StatusIcon size={13} strokeWidth={2.4} />
                    {meta.label}
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'relative', zIndex: 1, display: 'grid', gap: '14px', mt: '12px',
                    [MQ_860]: { gridTemplateColumns: '1fr auto', alignItems: 'end', gap: '24px' },
                }}
            >
                <Box>
                    {isCancelled && (
                        <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'error.bg', color: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '10px' }}>
                            <XCircle size={22} />
                        </Box>
                    )}
                    <Typography
                        component="h1"
                        sx={{ m: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.2, [MQ_520]: { fontSize: 26 } }}
                    >
                        {meta.title}
                    </Typography>
                    <Typography sx={{ mt: '6px', fontSize: 13, lineHeight: 1.5, color: 'var(--h-fg2)' }}>{meta.msg}</Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', [MQ_860]: { gridTemplateColumns: 'repeat(2,minmax(150px,1fr))' } }}>
                    <HeroTile icon={<Calendar size={12} />} label="Entrada">
                        {entrada
                            ? <><b>{entrada.dow}, {entrada.date}</b>{entrada.time && <small>às {entrada.time}</small>}</>
                            : <b>—</b>}
                    </HeroTile>
                    <HeroTile icon={<Clock size={12} />} label="Entrega prevista">
                        {entrega
                            ? <><b>{entrega.dow}, {entrega.date}</b>{entrega.time && <small>até {entrega.time}</small>}</>
                            : <b>A definir</b>}
                    </HeroTile>
                </Box>

                {meta.step >= 0 && <Stepper current={meta.step} paid={meta.key === 'paga'} />}
            </Box>

            <Box
                sx={{
                    position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                    mt: '14px', pt: '12px', borderTop: '1px solid var(--h-tile-b)', fontSize: 11.5, color: 'var(--h-fg2)',
                }}
            >
                <span>{updatedAt ? `Atualizado às ${updatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                <ButtonBase
                    className="no-print"
                    onClick={onRefresh}
                    disabled={refreshing}
                    aria-label="Atualizar andamento da ordem"
                    sx={(t) => ({
                        display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: 'var(--h-tile)',
                        border: '1px solid var(--h-tile-b)', color: 'var(--h-fg)', px: '11px', py: '6px', borderRadius: '20px',
                        fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', transition: 'background-color .15s, border-color .15s',
                        '&:hover': isCancelled
                            ? { bgcolor: 'background.subtle', borderColor: 'primary.main' }
                            : { bgcolor: 'rgba(255,255,255,.24)' },
                        '&.Mui-disabled': { color: 'var(--h-fg)', opacity: 0.85 },
                        '& svg': refreshing ? { animation: 'fv-spin .8s linear infinite' } : undefined,
                        '@keyframes fv-spin': { to: { transform: 'rotate(360deg)' } },
                        '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                    })}
                >
                    <RefreshCw size={13} />
                    Atualizar
                </ButtonBase>
            </Box>
        </Box>
    );
}

function HeroTile({ icon, label, children }) {
    return (
        <Box sx={{ bgcolor: 'var(--h-tile)', border: '1px solid var(--h-tile-b)', borderRadius: '12px', p: '10px 12px' }}>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--h-fg2)', mb: '4px' }}>
                {icon}
                {label}
            </Box>
            <Box sx={{ '& b': { display: 'block', fontSize: 13.5, fontWeight: 700 }, '& small': { fontSize: 11, color: 'var(--h-fg2)' } }}>
                {children}
            </Box>
        </Box>
    );
}

/** Stepper de 4 etapas derivado do status atual (sem timestamps — a API não devolve histórico). */
function Stepper({ current, paid }) {
    return (
        <Box
            role="list"
            aria-label="Andamento da ordem"
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', mt: '2px', [MQ_860]: { gridColumn: '1/-1' } }}
        >
            {STEP_LABELS.map((label, i) => {
                const done = i < current || (paid && i === 3);
                const active = !done && i === current;
                const lit = done || active;
                return (
                    <Box
                        key={label}
                        role="listitem"
                        aria-current={active ? 'step' : undefined}
                        sx={{
                            position: 'relative', textAlign: 'center', fontSize: 10.5, fontWeight: 700, lineHeight: 1.25,
                            px: '2px', color: lit ? 'var(--h-fg)' : 'var(--h-fg2)',
                            ...(i > 0 && {
                                '&::before': {
                                    content: '""', position: 'absolute', top: 11, right: '50%', width: '100%', height: 3, zIndex: 0,
                                    bgcolor: lit ? 'var(--h-fg)' : 'var(--h-tile-b)',
                                },
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                width: 26, height: 26, borderRadius: '50%', mx: 'auto', mb: '7px', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1,
                                color: 'var(--h-ink)',
                                bgcolor: lit ? 'var(--h-fg)' : 'var(--h-tile)',
                                border: '2px solid', borderColor: lit ? 'var(--h-fg)' : 'var(--h-tile-b)',
                                ...(active && { boxShadow: '0 0 0 5px rgba(255,255,255,.28)' }),
                            }}
                        >
                            {done && <Check size={13} strokeWidth={3} />}
                            {active && (
                                <Box
                                    component="i"
                                    sx={{
                                        width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--h-ink)',
                                        animation: 'fv-pulse 1.6s ease-in-out infinite',
                                        '@keyframes fv-pulse': {
                                            '0%,100%': { transform: 'scale(1)', opacity: 1 },
                                            '50%': { transform: 'scale(.6)', opacity: 0.6 },
                                        },
                                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                                    }}
                                />
                            )}
                        </Box>
                        {label}
                    </Box>
                );
            })}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function Card({ children, sx, ...rest }) {
    return (
        <Box
            component="section"
            sx={[
                (t) => ({
                    bgcolor: 'background.paper', border: `1px solid ${t.palette.divider}`, borderRadius: '14px',
                    boxShadow: t.customShadows.sm, p: '16px', [MQ_520]: { p: '20px' },
                }),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...rest}
        >
            {children}
        </Box>
    );
}

function CardHead({ icon, title, count, tone = 'info', sx }) {
    const purple = tone === 'purple';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '14px', ...sx }}>
            <Box
                sx={{
                    width: 30, height: 30, borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: purple ? 'secondary.bg' : 'info.bg', color: purple ? 'secondary.main' : 'primary.main',
                }}
            >
                {icon}
            </Box>
            <Typography component="h2" sx={{ m: 0, fontSize: 14, fontWeight: 700, flex: 1 }}>{title}</Typography>
            {count && (
                <Box
                    component="span"
                    sx={(t) => ({
                        fontSize: 11, fontWeight: 700, color: 'text.disabled', bgcolor: 'background.subtle',
                        border: `1px solid ${t.palette.divider}`, px: '9px', py: '3px', borderRadius: '20px',
                    })}
                >
                    {count}
                </Box>
            )}
        </Box>
    );
}

function KeyValue({ label, value, full, boxed }) {
    return (
        <Box
            sx={(t) => ({
                ...(full && { gridColumn: '1/-1' }),
                ...(boxed && {
                    bgcolor: 'background.subtle', border: `1px solid ${t.palette.divider}`, borderRadius: '11px', p: '10px 12px',
                }),
            })}
        >
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', mb: '3px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, wordBreak: 'break-word' }}>{value ?? 'Não informado'}</Typography>
        </Box>
    );
}

function VehicleCard({ order }) {
    const type = String(order.vehicle_type || '').toLowerCase();
    const isBike = type === 'motorcycle' || type === 'bicycle';
    const typeLabel = { car: 'Carro', motorcycle: 'Moto', bicycle: 'Bicicleta' }[type] || 'Veículo';
    const fab = order.vehicle_fab_year;
    const mod = order.vehicle_mod_year;
    const years = fab ? (mod && mod !== fab ? `${fab}/${mod}` : `${fab}`) : '';
    const km = typeof order.vehicle_km === 'number' ? order.vehicle_km.toLocaleString('pt-BR') : order.vehicle_km;

    return (
        <Card sx={{ order: 1 }}>
            <CardHead icon={isBike ? <Bike size={16} /> : <Car size={16} />} title="Seu veículo" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: '14px' }}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.2 }}>
                    {[order.vehicle_manufacturer, order.vehicle_model].filter(Boolean).join(' ')}
                    <Box component="small" sx={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'text.disabled', letterSpacing: 0, mt: '3px' }}>
                        {typeLabel}{years ? ` · ${years}` : ''}
                    </Box>
                </Typography>
                {order.vehicle_license_plate && (
                    <Box
                        sx={(t) => ({
                            border: `2px solid ${t.palette.mode === 'dark' ? '#0f172a' : t.palette.text.primary}`,
                            borderTop: `7px solid ${t.palette.primary.main}`, borderRadius: '7px', p: '3px 11px 2px',
                            fontWeight: 800, fontSize: 16, letterSpacing: '.12em', bgcolor: '#fff', color: '#0f172a', flexShrink: 0,
                        })}
                    >
                        {order.vehicle_license_plate}
                    </Box>
                )}
            </Box>
            {(km || order.vehicle_color) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', [MQ_520]: { gridTemplateColumns: 'repeat(4,1fr)' } }}>
                    {km ? <KeyValue boxed label="Quilometragem" value={`${km} km`} /> : null}
                    {order.vehicle_color ? <KeyValue boxed label="Cor" value={order.vehicle_color} /> : null}
                </Box>
            )}
        </Card>
    );
}

function SectionLabel({ children, first }) {
    return (
        <Box
            sx={{
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
                textTransform: 'uppercase', color: 'text.disabled', m: first ? '0 0 8px' : '16px 0 8px',
                '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider' },
            }}
        >
            {children}
        </Box>
    );
}

/**
 * Linha de item (serviço/produto). Serviço com `tasks` (proposta do protótipo,
 * a API pública não devolve hoje) vira accordion; sem dado é linha simples.
 */
function ItemRow({ item, isService }) {
    const [open, setOpen] = useState(false);
    const tasks = isService && Array.isArray(item.tasks) && item.tasks.length > 0 ? item.tasks : null;
    const doneCount = tasks ? tasks.filter((task) => task.done).length : 0;
    const discount = !isService && parseMoney(item.item_discount_amount) > 0 ? item.item_discount_amount : null;

    const main = (
        <>
            <Box
                sx={{
                    width: 38, height: 38, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isService ? 'secondary.bg' : 'info.bg', color: isService ? 'secondary.main' : 'primary.main',
                }}
            >
                {isService ? <Wrench size={17} /> : <Package size={17} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word', [MQ_860]: { fontSize: 14 } }}>
                    {item.name}
                </Typography>
                <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>{item.quantity} × {formatCurrency(item.amount_unit)}</span>
                    {tasks && (
                        <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: 'secondary.main', bgcolor: 'secondary.bg', px: '8px', py: '2px', borderRadius: '20px' }}>
                            {doneCount}/{tasks.length} tarefas
                        </Box>
                    )}
                </Box>
                {discount && (
                    <Typography sx={{ fontSize: 11, color: 'error.main', mt: '2px', fontWeight: 600 }}>
                        Desconto no item: −{formatCurrency(discount)}
                    </Typography>
                )}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5, flexShrink: 0, textAlign: 'right' }}>
                {formatCurrency(item.amount_total)}
            </Typography>
            {tasks && (
                <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>
                    <ChevronDown size={16} />
                </Box>
            )}
        </>
    );

    return (
        <Box sx={(t) => ({ border: `1px solid ${t.palette.divider}`, borderRadius: '12px', mb: '8px', overflow: 'hidden', bgcolor: 'background.paper' })}>
            {tasks ? (
                <ButtonBase
                    onClick={() => setOpen((prev) => !prev)}
                    aria-expanded={open}
                    sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '12px', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', '&:hover': { bgcolor: 'background.subtle' } }}
                >
                    {main}
                </ButtonBase>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '12px' }}>{main}</Box>
            )}
            {tasks && (
                <Collapse in={open} timeout={300} unmountOnExit>
                    <Box sx={(t) => ({ p: '2px 14px 14px 62px', borderTop: `1px solid ${t.palette.divider}`, bgcolor: 'background.subtle' })}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', m: '12px 0 8px' }}>
                            Andamento do serviço
                        </Typography>
                        {tasks.map((task, idx) => (
                            <Box
                                key={idx}
                                sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontSize: 12.5, py: '5px', color: task.done ? 'text.primary' : 'text.secondary' }}
                            >
                                <Box
                                    sx={{
                                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', border: '2px solid', borderColor: task.done ? 'success.main' : 'divider',
                                        bgcolor: task.done ? 'success.main' : 'transparent',
                                    }}
                                >
                                    {task.done && <Check size={10} strokeWidth={3.4} />}
                                </Box>
                                {task.name}
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
}

function SummaryCard({ values, showDiscount, muted }) {
    return (
        <Card sx={{ order: 4 }}>
            <CardHead icon={<FileText size={16} />} title="Resumo de valores" />
            <SummaryRow label="Serviços" value={formatCurrency(values.amount_services)} />
            <SummaryRow label="Produtos e peças" value={formatCurrency(values.amount_products)} />
            {showDiscount && (
                <SummaryRow discount label="Desconto em produtos" value={`−${formatCurrency(values.total_product_item_discount)}`} />
            )}
            <Box
                sx={(t) => ({
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mt: '8px', p: '14px', borderRadius: '14px',
                    border: '1.5px solid', ...(muted
                        ? { bgcolor: 'background.subtle', borderColor: 'divider', color: 'text.disabled' }
                        : { bgcolor: 'success.bg', borderColor: 'success.main', color: 'success.main' }),
                })}
            >
                <Box component="span" sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>Total</Box>
                <Box component="b" sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>{formatCurrency(values.amount)}</Box>
            </Box>
        </Card>
    );
}

function SummaryRow({ label, value, discount }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: 13, py: '7px', color: discount ? 'error.main' : 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: discount ? 'error.main' : 'text.primary', fontWeight: 700, whiteSpace: 'nowrap' }}>{value}</Box>
        </Box>
    );
}

function TermsCard({ terms }) {
    const [open, setOpen] = useState(false);
    return (
        <Card sx={{ order: 6 }}>
            <ButtonBase
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', borderRadius: '8px' }}
            >
                <CardHead icon={<FileText size={16} />} title="Termos e condições" sx={{ mb: 0, flex: 1 }} />
                <Box sx={{ color: 'text.disabled', display: 'flex', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>
                    <ChevronDown size={16} />
                </Box>
            </ButtonBase>
            <Collapse in={open} timeout={300}>
                <Box
                    sx={(t) => ({
                        mt: '12px', fontSize: 12.5, lineHeight: 1.65, color: 'text.secondary', whiteSpace: 'pre-wrap',
                        bgcolor: 'background.subtle', border: `1px solid ${t.palette.divider}`, borderRadius: '11px', p: '12px 14px',
                    })}
                >
                    {terms}
                </Box>
            </Collapse>
        </Card>
    );
}

function ContactButton({ kind, href, large }) {
    const whatsapp = kind === 'whatsapp';
    return (
        <Button
            component="a"
            href={href}
            {...(whatsapp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            variant={whatsapp ? 'contained' : 'outlined'}
            color={whatsapp ? 'success' : 'primary'}
            startIcon={whatsapp ? <MessageCircle size={large ? 17 : 16} /> : <Phone size={large ? 17 : 16} />}
            sx={{
                flex: 1, whiteSpace: 'nowrap', fontSize: 13, fontWeight: 700, borderRadius: '11px',
                p: large ? '12px 14px' : '11px 16px',
                ...(whatsapp && { color: '#fff', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.main', filter: 'brightness(1.06)' } }),
            }}
        >
            {whatsapp ? 'WhatsApp' : 'Ligar'}
        </Button>
    );
}

function CompanyCard({ order, addressText, waHref, telHref }) {
    return (
        <Card sx={{ order: 7 }}>
            <CardHead icon={<Building2 size={16} />} title="Fale com a empresa" />
            {(waHref || telHref) && (
                <Box sx={{ display: 'none', gap: '8px', mb: '14px', [MQ_860]: { display: 'flex' } }}>
                    {waHref && <ContactButton kind="whatsapp" href={waHref} />}
                    {telHref && <ContactButton kind="phone" href={telHref} />}
                </Box>
            )}
            <InfoLine icon={<MapPin size={15} />}>{addressText || 'Endereço não informado'}</InfoLine>
            {order.enterprise_cellphone && (
                <InfoLine icon={<Phone size={15} />}>
                    {formatPhone(order.enterprise_cellphone)} <Box component="span" sx={{ color: 'text.disabled' }}>(celular)</Box>
                </InfoLine>
            )}
            {order.enterprise_phone_number && (
                <InfoLine icon={<Phone size={15} />}>
                    {formatPhone(order.enterprise_phone_number)} <Box component="span" sx={{ color: 'text.disabled' }}>(fixo)</Box>
                </InfoLine>
            )}
            {order.enterprise_facebook && <InfoLine icon={<Globe size={15} />}>{order.enterprise_facebook}</InfoLine>}
            {order.enterprise_instagram && <InfoLine icon={<AtSign size={15} />}>{order.enterprise_instagram}</InfoLine>}
        </Card>
    );
}

function InfoLine({ icon, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: 12.5, color: 'text.secondary', py: '7px', lineHeight: 1.45 }}>
            <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0, mt: '1px' }}>{icon}</Box>
            <span>{children}</span>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Estados: carregando (skeleton) e feedback (link inválido / não encontrada / erro)
// ---------------------------------------------------------------------------

function Skel({ h, w = '100%', mb = 0, round, sx }) {
    return (
        <Box
            sx={{
                height: h, width: w, mb: `${mb}px`, borderRadius: round ? '14px' : '8px',
                backgroundImage: (t) => `linear-gradient(90deg, ${t.palette.background.subtle} 0%, ${t.palette.divider} 50%, ${t.palette.background.subtle} 100%)`,
                backgroundSize: '200% 100%', animation: 'fv-shimmer 1.3s ease-in-out infinite',
                '@keyframes fv-shimmer': { from: { backgroundPosition: '200% 0' }, to: { backgroundPosition: '-200% 0' } },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                ...sx,
            }}
        />
    );
}

function LoadingView() {
    return (
        <>
            <Box component="header" sx={(t) => ({ bgcolor: 'background.paper', borderBottom: `1px solid ${t.palette.divider}` })}>
                <Box sx={{ maxWidth: 1040, mx: 'auto', display: 'flex', alignItems: 'center', gap: '12px', p: '14px 16px', [MQ_520]: { p: '16px 24px' } }}>
                    <Skel h={48} w={48} round sx={{ flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                        <Skel h={14} w="60%" mb={8} />
                        <Skel h={10} w="35%" />
                    </Box>
                </Box>
            </Box>
            <Box
                component="main"
                aria-busy="true"
                aria-live="polite"
                sx={{
                    width: '100%', maxWidth: 1040, mx: 'auto', display: 'grid', gap: '14px', p: '16px 16px 24px',
                    [MQ_520]: { p: '20px 24px 28px' }, [MQ_860]: { gap: '18px', p: '24px 24px 32px' },
                }}
            >
                <Card sx={{ p: '20px' }}>
                    <Skel h={11} w="34%" mb={16} />
                    <Skel h={24} w="62%" mb={12} />
                    <Skel h={12} w="90%" mb={6} />
                    <Skel h={12} w="70%" mb={22} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Skel h={56} />
                        <Skel h={56} />
                    </Box>
                </Card>
                <Card>
                    <Skel h={14} w="30%" mb={16} />
                    <Skel h={54} mb={8} />
                    <Skel h={54} />
                </Card>
                <Card>
                    <Skel h={14} w="40%" mb={16} />
                    <Skel h={44} mb={8} />
                    <Skel h={44} mb={8} />
                    <Skel h={44} />
                </Card>
                <Typography sx={{ textAlign: 'center', fontSize: 12.5, color: 'text.disabled', fontWeight: 600 }}>
                    Carregando sua ordem de serviço…
                </Typography>
            </Box>
        </>
    );
}

const FEEDBACK = {
    invalid: {
        tone: 'warning', Icon: Unlink, title: 'Este link não é válido',
        text: 'O link pode estar incompleto, ter sido alterado ou ter expirado.',
        help: [
            <>Confira se copiou o link inteiro, até o final (incluindo o código depois de <b>?t=</b>).</>,
            'Abra o link direto da mensagem que você recebeu.',
            'Se continuar com problema, peça um novo link à empresa.',
        ],
    },
    notfound: {
        tone: 'neutral', Icon: SearchX, title: 'Ordem de serviço não encontrada',
        text: 'Não localizamos nenhuma ordem com esse número. O número pode estar incorreto ou a ordem pode ter sido removida.',
        help: [
            'Confira o número da ordem na mensagem ou no comprovante que você recebeu.',
            'Entre em contato com a empresa que enviou o link.',
        ],
    },
    error: {
        tone: 'danger', Icon: WifiOff, title: 'Não foi possível carregar',
        text: 'Houve um problema de conexão ou de nosso lado. Sua ordem não foi alterada.',
        help: null, retry: true,
    },
};

function FeedbackView({ kind, onRetry }) {
    const cfg = FEEDBACK[kind] || FEEDBACK.error;
    const { Icon } = cfg;
    const toneSx = {
        warning: { borderColor: 'warning.main', bgcolor: 'warning.bg', color: 'warning.main', borderStyle: 'solid' },
        danger: { borderColor: 'error.main', bgcolor: 'error.bg', color: 'error.main', borderStyle: 'solid' },
        neutral: { borderColor: 'divider', color: 'text.disabled', borderStyle: 'dashed' },
    }[cfg.tone];

    return (
        <>
            <Box
                component="main"
                role="alert"
                sx={{ maxWidth: 460, width: '100%', mx: 'auto', p: '56px 20px 30px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
                <Box sx={{ width: 78, height: 78, borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', ...toneSx }}>
                    <Icon size={32} strokeWidth={1.8} />
                </Box>
                <Typography component="h1" sx={{ m: 0, mb: '8px', fontSize: 20, fontWeight: 800, letterSpacing: '-.02em' }}>{cfg.title}</Typography>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>{cfg.text}</Typography>
                {cfg.help && (
                    <Box
                        sx={(t) => ({
                            mt: '20px', textAlign: 'left', width: '100%', bgcolor: 'background.paper', border: `1px solid ${t.palette.divider}`,
                            borderRadius: '12px', p: '14px 16px', fontSize: 12.5, color: 'text.secondary',
                        })}
                    >
                        <Box sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '8px' }}>
                            O que fazer
                        </Box>
                        <Box component="ul" sx={{ m: 0, pl: '18px', lineHeight: 1.7, '& b': { color: 'text.primary' } }}>
                            {cfg.help.map((item, idx) => <li key={idx}>{item}</li>)}
                        </Box>
                    </Box>
                )}
                {cfg.retry && (
                    <Button variant="contained" onClick={onRetry} startIcon={<RefreshCw size={15} />} sx={{ mt: '20px', p: '11px 16px', fontSize: 13, fontWeight: 700, borderRadius: '11px' }}>
                        Tentar novamente
                    </Button>
                )}
            </Box>
            <PoweredBy />
        </>
    );
}
